import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Avatar, Box, Chip, Snackbar, Alert, Tooltip, Typography } from '@mui/material';
import ColorConstants from '../../../defaults/ColorConstants';
import ValuesToolbar from './ValuesToolbar.tsx';
import MuiGridWithConnector from '../../MuiGridWithConnector.tsx';
import { getAppConfig, getAttributeServiceUrl } from '../../../utils/ReduxHelper.jsx';
import {
    type GridColumnVisibilityModel,
    type GridDataSource,
    type GridFilterModel,
    type GridRowParams,
    useGridApiRef,
} from '@mui/x-data-grid';
import { configureDataSourceAndFields } from '../../../utils/BulkEditHelper.tsx';
import type { GridFieldConfiguration } from '../../../utils/GridColumnHelper.ts';
import { emitCustomEvent, useCustomEventListener } from 'react-custom-events';
import Events from '../../../defaults/Events';
import FieldNames from '../../../defaults/FieldNames.json';
import ValuesDetailsPanel from './ValuesDetailsPanel.tsx';
import Query from '@arcgis/core/rest/support/Query';
import * as query from '@arcgis/core/rest/query';
import { useDispatch, useSelector } from 'react-redux';
import FeatureService from '../../../services/FeatureService';
import { GridActionsCellItem } from '@mui/x-data-grid-pro';
import { Delete, Transform } from '@mui/icons-material';
import * as geodeticAreaOperator from '@arcgis/core/geometry/operators/geodeticAreaOperator';
import { GRID_ROOT_GROUP_ID } from '@mui/x-data-grid';
import Polygon from '@arcgis/core/geometry/Polygon';
import SpatialReference from '@arcgis/core/geometry/SpatialReference';
import * as projectOperator from '@arcgis/core/geometry/operators/projectOperator';
import {
    setIsEditingPostFireValue,
    setStoredPostFireValues,
    clearStoredPostFireValues
} from '../../../redux/slices/detailsSlice';
import { generateFeatureCollectionFromShapefile } from '../../../utils/UploadHelper';
import { setAlertMessageObj } from '../../../redux/slices/globalContextSlice';


export default function ValuesPanel({ joinId, joinFeature, setHasPostFireValues, hasActual }) {
    const [isAddingValue, setIsAddingValue] = useState(false);
    const [dataSource, setDataSource] = useState<GridDataSource | null>(null);
    const config: any = getAppConfig();
    const apiRef = useGridApiRef();
    const [isSaving] = useState(false);
    const [selectedRow, setSelectedRow] = useState<any>(null);
    const [isEditingValue, setIsEditingValue] = useState(false);
    const [newValueGeometry, setNewValueGeometry] = useState<any>(null);
    const [isEditingGeometry, setIsEditingGeometry] = useState(false);
    const [editingGeometryRow, setEditingGeometryRow] = useState<any>(null);
    const [attributesGlobalId, setAttributesGlobalId] = useState<string | null>(null);
    const mode = useSelector((state) => state.details.mode);
    const [maxRank, setMaxRank] = useState<number>(1);
    const [colDefs, setColDefs] = useState<any>([]);
    const isTreatment = useSelector((state) => state.details.isTreatment);

    const [columnVisibilityModel] = useState<GridColumnVisibilityModel>({});
    const [filterModel] = useState<GridFilterModel | null>(null);
    const modifiedGeometryRef = useRef<any>(null);
    const dispatch = useDispatch();
    const isActual = useSelector((state) => state.details.isActual);
    const storedPostFireValues = useSelector((state) => state.details.storedPostFireValues);
    const [isDisabled, setIsDisabled] = useState(false);
    const [isSelectingWFIGS, setIsSelectingWFIGS] = useState(false);
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
        open: false, message: '', severity: 'success'
    });

    // Consolidate disabled logic - disable when not in edit mode OR when it's an estimated feature with an actual
    useEffect(() => {
        const shouldDisable =
            !joinId ||
            !joinFeature ||
            !attributesGlobalId ||
            mode !== 'edit' ||
            (!isActual && hasActual);
        setIsDisabled(shouldDisable);
    }, [joinId, joinFeature, attributesGlobalId, mode, isActual, hasActual]);

    // Listen for geometry changes from the draw tools
    useCustomEventListener(Events.ModifiedGraphicChanged, (graphic: any) => {
        if (isEditingGeometry) {
            modifiedGeometryRef.current = graphic?.geometry || graphic;
        }
    });


    // Listen for request to copy stored postfire values to new actual
    useCustomEventListener(Events.CopyStoredPostFireValuesToActual, async (data: any) => {
        const { newJoinId, newJoinFeature } = data;
        if (!newJoinId || !newJoinFeature || storedPostFireValues.length === 0) return;

        try {
            // Get the new attributesGlobalId for the actual feature
            const newAttributesGlobalId = await getAttributesGlobalid(newJoinId, newJoinFeature);
            if (!newAttributesGlobalId) {
                console.error('Could not get attributesGlobalId for new actual feature');
                return;
            }

            // Create new features with updated attributesGlobalId
            const newFeatures = storedPostFireValues.map((feature: any) => {
                const { GlobalID, OBJECTID, ...otherAttributes } = feature.attributes;
                return {
                    attributes: {
                        ...otherAttributes,
                        [FieldNames.postFireValuesFields.attributesGlobalID]: newAttributesGlobalId,
                    },
                    geometry: feature.geometry,
                };
            });

            // Add the features to the service
            const serviceUrl = getAttributeServiceUrl();
            const addResponse = await FeatureService.addFeatures(
                serviceUrl,
                config.postfire[config.env].valuesPolygons.layerId,
                newFeatures,
                false
            );

            if (addResponse?.data?.addResults?.length > 0) {
                console.log(`Successfully copied ${addResponse.data.addResults.length} postfire values to new actual`);
                // Clear stored values in Redux
                dispatch(clearStoredPostFireValues());
                // Refresh the grid to show the new values
                emitCustomEvent(Events.RefreshPostFireValuesLayer);
            }
        } catch (error) {
            console.error('Error copying postfire values to new actual:', error);
        }
    });

    function handleValueGeometry(projectedGeometry: any, source: string) {
        if (isEditingGeometry && editingGeometryRow) {
            const globalId = editingGeometryRow[FieldNames.postFireValuesFields.globalID];
            const geometryJson = projectedGeometry.toJSON ? projectedGeometry.toJSON() : projectedGeometry;
            const update = {
                attributes: {
                    [FieldNames.postFireValuesFields.globalID]: globalId,
                    [FieldNames.postFireValuesFields.objectId]: editingGeometryRow[FieldNames.postFireValuesFields.objectId],
                },
                geometry: geometryJson,
            };

            updateFeatures([update]).then(() => {
                refreshGrid();
                emitCustomEvent(Events.RefreshPostFireValuesLayer);
                setIsEditingGeometry(false);
                setEditingGeometryRow(null);
                modifiedGeometryRef.current = null;
                emitCustomEvent(Events.DisableDrawMode);
                dispatch(setIsEditingPostFireValue(false));
                setSnackbar({ open: true, message: `Value geometry updated from ${source}`, severity: 'success' });
            }).catch((err) => {
                console.error(`Error updating value geometry from ${source}:`, err);
                setSnackbar({ open: true, message: 'Error updating value geometry', severity: 'error' });
            });
        } else {
            setIsAddingValue(false);
            setNewValueGeometry(projectedGeometry);
            setSelectedRow({});
            setIsEditingValue(true);
            dispatch(setIsEditingPostFireValue(false));
            setSnackbar({ open: true, message: `${source === 'shapefile' ? 'Shapefile uploaded' : 'Fire perimeter copied'} successfully`, severity: 'success' });
        }
    }

    // Listen for WFIGS feature selected for value copy
    useCustomEventListener(Events.WFIGSFeatureSelectedForValue, async (data: any) => {
        if (!isSelectingWFIGS) return;
        setIsSelectingWFIGS(false);

        try {
            const geometry = data?.geometry;
            if (!geometry) {
                console.error('No geometry received from WFIGS feature');
                return;
            }

            // Project to Web Mercator if needed (WFIGS is in WGS84/4326)
            let projectedGeometry = geometry;
            if (geometry.spatialReference?.wkid !== 102100 && geometry.spatialReference?.wkid !== 3857) {
                await projectOperator.load();
                const polygon = new Polygon({
                    rings: geometry.rings,
                    spatialReference: geometry.spatialReference,
                });
                projectedGeometry = projectOperator.execute(polygon, new SpatialReference({ wkid: 102100 }));
            }

            handleValueGeometry(projectedGeometry, 'fire perimeter');
        } catch (error) {
            console.error('Error processing WFIGS feature for value:', error);
            setIsAddingValue(false);
            dispatch(setIsEditingPostFireValue(false));
            setSnackbar({ open: true, message: 'Error copying fire perimeter', severity: 'error' });
        }
    }, [isSelectingWFIGS, isEditingGeometry, editingGeometryRow]);

    const columns = useMemo(() => {

        if(colDefs.length){
            return [
                {
                    field: 'actions',
                    type: 'actions',
                    width: 120,
                    renderCell: (params: any) => <Actions params={params} disabled={isDisabled}></Actions>,
                },
                ...colDefs,
            ];
        }
        return []

    }, [colDefs, isDisabled]);

    const handleAddValueClicked = () => {
        if (apiRef.current) {
            setMaxRank(apiRef.current?.getRowsCount() + 1);
        }
        dispatch(setIsEditingPostFireValue(true));
        setIsAddingValue(true);
    };

    const handleAddValueCancel = useCallback(() => {
        setIsAddingValue(false);
        setNewValueGeometry(null);
        setIsSelectingWFIGS(false);
        // Disable draw mode on cancel
        emitCustomEvent(Events.DisableDrawMode);
        emitCustomEvent(Events.CancelWFIGSFeatureSelection);
        dispatch(setIsEditingPostFireValue(false));
    }, []);

    const handleDrawComplete = useCallback((geometry: any) => {
        // Store the drawn geometry
        setNewValueGeometry(geometry);
        // Stop adding mode (removes toolbar)
        setIsAddingValue(false);
        // Open the detail modal for the new value
        setSelectedRow({}); // Empty row for new value
        setIsEditingValue(true);
        dispatch(setIsEditingPostFireValue(false));
    }, []);

    const handleCopyFromFirePerimeter = useCallback(() => {
        setIsSelectingWFIGS(true);
        // Emit event to enable WFIGS feature selection on the map
        emitCustomEvent(Events.SelectWFIGSFeatureForValue);
    }, []);

    const handleShapefileSelected = useCallback(async (file: File) => {
        try {
            const tempPerimeter = await generateFeatureCollectionFromShapefile(file, config);
            if (!tempPerimeter) {
                dispatch(setAlertMessageObj({
                    message: 'Shapefile Parse Error',
                    severity: 'error',
                    child: 'Unable to parse shapefile or found empty geometry. Please verify .zip contents and geometry and try again.',
                    callback: null,
                }));
                return;
            }

            if (tempPerimeter.type !== 'polygon') {
                dispatch(setAlertMessageObj({
                    message: 'Geometry Type Error',
                    severity: 'error',
                    child: `Expecting polygon upload, received ${tempPerimeter.type}. Please upload a polygon shapefile.`,
                    callback: null,
                }));
                return;
            }

            // Project to Web Mercator if needed
            let projectedGeometry = tempPerimeter;
            if (tempPerimeter.spatialReference?.wkid !== 102100 && tempPerimeter.spatialReference?.wkid !== 3857) {
                await projectOperator.load();
                const polygon = new Polygon({
                    rings: tempPerimeter.rings,
                    spatialReference: tempPerimeter.spatialReference,
                });
                projectedGeometry = projectOperator.execute(polygon, new SpatialReference({ wkid: 102100 }));
            }

            handleValueGeometry(projectedGeometry, 'shapefile');
        } catch (error) {
            console.error('Error processing shapefile for value:', error);
            dispatch(setAlertMessageObj({
                message: 'Shapefile Parse Error',
                severity: 'error',
                child: 'Unable to parse shapefile or found empty geometry. Please verify .zip contents and geometry and try again.',
                callback: null,
            }));
        }
    }, [config, isEditingGeometry, editingGeometryRow]);

    const handleEditValue = useCallback((row: any) => {
        setSelectedRow(row);
        setIsEditingValue(true);
        dispatch(setIsEditingPostFireValue(true));
    }, []);

    const handleCloseValuesDetail = useCallback(() => {
        setIsEditingValue(false);
        setSelectedRow(null);
        setNewValueGeometry(null);
        dispatch(setIsEditingPostFireValue(false));
    }, []);

    const handleCancelGeometryEdit = useCallback(() => {
        setIsEditingGeometry(false);
        setEditingGeometryRow(null);
        modifiedGeometryRef.current = null;
        setIsSelectingWFIGS(false);
        emitCustomEvent(Events.DisableDrawMode);
        emitCustomEvent(Events.CancelWFIGSFeatureSelection);
        dispatch(setIsEditingPostFireValue(false));
    }, []);

    const handleEditGeometry = useCallback(
        async (row: any) => {
            // Load the geometry for this value row from the service
            try {
                if (row) {
                    dispatch(setIsEditingPostFireValue(true));
                    const geom = row._geometry;
                    setEditingGeometryRow({ ...row, geometry: geom });
                    setIsEditingGeometry(true);

                    // Enable draw mode with the existing geometry
                    emitCustomEvent(Events.ToggleShouldUpdateGraphic, true);
                    emitCustomEvent(Events.EnableDrawMode, {
                        geometry: new Polygon(geom),
                        attributes: row,
                    });
                }
            } catch (error) {
                console.error('Error loading value geometry:', error);

            }
        },
        [config]
    );

    const updateFeatures = (updates: any[]) => {
        const serviceUrl = getAttributeServiceUrl();
        return FeatureService.updateFeatures(
            serviceUrl,
            config.postfire[config.env].valuesPolygons.layerId,
            updates,
            true
        );
    };

    const addFeatures = (adds: any[] ) => {
        const serviceUrl = getAttributeServiceUrl();
        return FeatureService.addFeatures(
            serviceUrl,
            config.postfire[config.env].valuesPolygons.layerId,
            adds,
            false
        );
    };

    const handleOnSaveComplete = useCallback(
        (newFeature: any) => {
            // update grid row, and if existing, get globalID, when ranking, if that row is the updated one, use that value, otherwise use the value from the grid, this is to ensure the correct rank is used when re-ranking rows below
            const existingFeatureId = newFeature.attributes[FieldNames.postFireValuesFields.globalID];
            if (existingFeatureId){
                apiRef.current?.updateRows([
                    {
                        ...newFeature,
                        rowId: existingFeatureId,
                    },
                ]);
            }
            const rowMap = apiRef.current?.getRowModels();
            let allRows = rowMap
                ? Array.from(rowMap.values()).map((r) => formatSaveableRow(r))
                : [];
            const newRank = parseInt(newFeature.attributes[FieldNames.postFireValuesFields.rank]);

            // // If editing an existing row, remove it from allRows and store its old rank
            if (!existingFeatureId) {
                // Add the new/updated row into the full set
                allRows.push(formatSaveableRow(newFeature));
            }

            // Shift existing rows with rank >= new rank up by 1
            reRankRows(allRows, newRank, existingFeatureId);

            // Normalize so ranks are sequential starting from 1
            normalizeRanks(allRows);

            const adds: any[] = [];
            const updates: any[] = [];

            allRows.forEach((row) => {
                if (row.attributes[FieldNames.postFireValuesFields.globalID]) {
                    updates.push(row);
                } else {
                    adds.push(row);
                }
            });

            const promises = [];
            if (adds.length > 0) {
                promises.push(addFeatures(adds));
            }

            if (updates.length > 0) {
                promises.push(updateFeatures(updates));
            }

            Promise.allSettled(promises).then(() => {
                handleCloseValuesDetail();
                refreshGrid();
                emitCustomEvent(Events.RefreshPostFireValuesLayer);
                dispatch(setIsEditingPostFireValue(false));
            });
        },
        [apiRef]
    );

    function formatSaveableRow(update: any) {
        // destructure out geom and rowId from attributes
        // Grid row models are flat (rowId, _geometry, and attribute fields at top level)
        // Feature objects have nested { attributes: {}, geometry: {} }
        const source = update.attributes ? update.attributes : update;
        const { _geometry, rowId, ...att } = source;
        const rowToSave: any = {
            attributes: att,
        };

        if (_geometry) {
            rowToSave.geometry = _geometry;
        }

        // If there's a geometry on the update object itself (feature format), include it
        if (!rowToSave.geometry && update.geometry) {
            rowToSave.geometry = update.geometry;
        }

        return rowToSave;
    }

    const refreshGrid = () => {
        // Clear the internal data source cache
        apiRef.current?.dataSource.cache.clear();
        // To bypass the cache and force a redraw
        apiRef.current?.dataSource.fetchRows(GRID_ROOT_GROUP_ID, {
            skipCache: true,
        });
        // reset sort model to rank
        apiRef.current?.setSortModel([
            { field: FieldNames.postFireValuesFields.rank, sort: 'asc' }, // or 'desc'
        ]);
    };


    const handleConfirmGeometryEdit = useCallback(async () => {
        try {
            if (!editingGeometryRow || !modifiedGeometryRef.current) {
                console.error('No geometry to save');
                return;
            }

            const globalId = editingGeometryRow[FieldNames.postFireValuesFields.globalID];
            const update = {
                attributes: {
                    [FieldNames.postFireValuesFields.globalID]: globalId,
                    [FieldNames.postFireValuesFields.objectId]: editingGeometryRow[FieldNames.postFireValuesFields.objectId]
                },
                geometry: modifiedGeometryRef.current.toJSON
                    ? modifiedGeometryRef.current.toJSON()
                    : modifiedGeometryRef.current,
            };

            // Update the feature
            updateFeatures([update]).then((resp: any) => {
                // Refresh the grid to show updated geometry
                refreshGrid();
                emitCustomEvent(Events.RefreshPostFireValuesLayer);

                // Clean up
                setIsEditingGeometry(false);
                setEditingGeometryRow(null);
                modifiedGeometryRef.current = null;
                emitCustomEvent(Events.DisableDrawMode);
                dispatch(setIsEditingPostFireValue(false));
            }).catch(err => {
                console.error(err)
            });


        } catch (error) {
            console.error('Error saving geometry:', error);
        }
    }, [editingGeometryRow, updateFeatures]);

    function reRankRows(rows: any[], replaceRankStart: number, existingGlobalId: string | null) {
        const rankField = FieldNames.postFireValuesFields.rank;
        rows.sort((a, b) => a.attributes[rankField] - b.attributes[rankField])
            .filter((row) => row.attributes[rankField] >= replaceRankStart)
            .forEach((row) => {
                if (row.attributes[FieldNames.postFireValuesFields.globalID] !== existingGlobalId) {
                    row.attributes[rankField] += 1;
                }
            });
        return rows;
    }

    function normalizeRanks(rows: any[]) {
        const rankField = FieldNames.postFireValuesFields.rank;
        rows.sort((a, b) => a.attributes[rankField] - b.attributes[rankField]).forEach(
            (row, index) => {
                row.attributes[rankField] = index + 1;
            }
        );
        return rows;
    }

    async function getAttributesGlobalid(joinId: number, joinFeature: string) {
        const attibutesrUrl = getAttributeServiceUrl();

        const queryObject = new Query({
            where: `${FieldNames.featureFields.joinID} = ${joinId} AND ${FieldNames.featureFields.joinFeature} = '${joinFeature}'`,
            outFields: [FieldNames.featureFields.globalID],
            returnGeometry: false,
        });

        return query
            .executeQueryJSON(
                attibutesrUrl + '/' + config.actionAttributes[config.env].layerId,
                queryObject
            )
            .then((results) => {
                if (results.features) {
                    return results.features[0].attributes[FieldNames.featureFields.globalID];
                }
                return null;
            })
            .catch((err) => {
                console.error('Error fetching attributes GlobalID:', err);
                return null;
            });
    }

    // after esri deprecated the sync geometryEngine.geodesicArea method, need to create a component to render area using new
    // geodeticAreaOperator, renderCell must be synchronous
    const AreaInAcres = ({ params} : { params: GridRowParams<any> }) => {
        const [area, setArea] = useState(0);
        useEffect(() => {
            const getArea = async () => {
                if (!geodeticAreaOperator.isLoaded()) {
                    await geodeticAreaOperator.load();
                }
                const area = geodeticAreaOperator.execute(params.row._geometry, {
                    unit: 'acres',
                });
                setArea(area);
            };
            getArea();
        }, [params.row]);

        return <>{area && area.toFixed(2)}</>;
    };

    const onRowClick = (params: GridRowParams<any>) => {
        if (mode === FieldNames.modes.edit) {
            setEditingGeometryRow({ ...params?.row, geometry: params.row?._geometry });
        }
        emitCustomEvent(Events.HighlightExistingFeature, {
            feature: { attributes: { ...params?.row } },
            alternateHighlight: true,
        });

        setTimeout(() => {
            emitCustomEvent(Events.RemoveExistingFeatureHighlight, { alternateHighlight: true });
        }, 5000);
    };

    const deleteFeature = (params: GridRowParams<any>) => {
        const serviceUrl = getAttributeServiceUrl();
        const promises = [];
        promises.push(
            FeatureService.deleteFeatures(
                serviceUrl,
                config.postfire[config.env].valuesPolygons.layerId,
                params.row[FieldNames.postFireValuesFields.objectId]
            )
        );
        apiRef.current?.updateRows([
            { rowId: params.row[FieldNames.postFireValuesFields.globalID], _action: 'delete' },
        ]);
        const updated = normalizeRanks(
            reRankRows(
                apiRef.current?.getRowModels()
                    ? Array.from(apiRef.current.getRowModels().values()).map((r) =>
                          formatSaveableRow(r)
                      )
                    : [],
                params.row[FieldNames.postFireValuesFields.rank],
                null // No existing feature since we're deleting
            )
        );
        promises.push(updateFeatures(updated));
        Promise.allSettled(promises).then(() => {
            refreshGrid();
            emitCustomEvent(Events.RefreshPostFireValuesLayer);
        });
    };
    const Actions = ({ params, disabled }: { params: GridRowParams<any>, disabled: boolean }) => {
        return (
            <>
                <Tooltip title='Edit value attributes' placement='top' arrow>
                    <GridActionsCellItem
                        key='update'
                        icon={<EditIcon />}
                        label='Update Value'
                        onClick={() => handleEditValue(params.row)}
                        disabled={disabled}
                    ></GridActionsCellItem>
                </Tooltip>
                <Tooltip title='Edit value shape' placement='top' arrow>
                    <GridActionsCellItem
                        key='modify'
                        icon={<Transform />}
                        label='Modify Shape'
                        disabled={disabled}
                        onClick={() => handleEditGeometry(params.row)}
                    ></GridActionsCellItem>
                </Tooltip>
                <Tooltip title='Delete value' placement='top' arrow>
                    <GridActionsCellItem
                        key='delete'
                        icon={<Delete />}
                        label='Delete Value'
                        disabled={disabled}
                        onClick={() => deleteFeature(params)}
                    ></GridActionsCellItem>
                </Tooltip>
            </>
        );
    };
    // Initialize data source and columns
    useEffect(() => {
        const initSetup = async () => {
            const attributesGlobalIDRequest = await getAttributesGlobalid(joinId, joinFeature);
            setAttributesGlobalId(attributesGlobalIDRequest);
            const layerId = config.postfire[config.env].valuesPolygons.layerId;

            // Build where clause to filter by parent feature's GlobalID
            const whereClause = `${FieldNames.postFireValuesFields.attributesGlobalID} = '${attributesGlobalIDRequest}'`

            const FF_PF = FieldNames.postFireValuesFields;

            const fields: GridFieldConfiguration[] = [
                { field: FF_PF.valueType, alias: 'Value Type' },
                { field: FF_PF.rank, alias: 'Rank' },

                {
                    field: FF_PF.area,
                    alias: 'Area (Acres)',
                    renderCell: (params) => <AreaInAcres params={params}></AreaInAcres>,
                },
            ];

            const { dataSource, colDefs } = await configureDataSourceAndFields(
                layerId,
                fields,
                whereClause,
                FieldNames.postFireValuesFields.globalID,
                undefined,
                true
            );

            setColDefs(colDefs)

            setDataSource(dataSource);
        };

        if (config && joinId && joinFeature) {
            initSetup();
        }
    }, [config, joinId, joinFeature, handleEditValue, handleEditGeometry]);

    // Subscribe to grid row count changes to update hasPostFireValues
    useEffect(() => {
        if (!apiRef.current) return;

        const unsubscribe = apiRef.current.subscribeEvent('rowsSet', () => {
            const rowCount = apiRef.current?.getRowsCount();
            setHasPostFireValues(rowCount ? rowCount > 0 : false);

            // Only store values for estimated treatments (not actuals, and only if no actual exists yet)
            if (!isActual && !hasActual) {
                if (rowCount && rowCount > 0) {
                    const rowMap = apiRef.current?.getRowModels();
                    if (rowMap) {
                        const valuesToStore = Array.from(rowMap.values()).map((row: any) => formatSaveableRow(row));
                        dispatch(setStoredPostFireValues(valuesToStore));
                    }
                } else {
                    dispatch(clearStoredPostFireValues());
                }
            }
        });

        return () => {
            unsubscribe();
        };
    }, [apiRef, dataSource, isActual, hasActual]);

    return (
        <Box sx={{ padding: '8px 0' }}>
            {/* Add Value button */}
            <Chip
                className='required-field-status-chip'
                onClick={handleAddValueClicked}
                style={{
                    backgroundColor: `${ColorConstants.AvatarBackgroundColor}`,
                    marginBottom: 8,
                    marginTop: 5,
                    marginLeft: 10,
                    zIndex: 5,
                }}
                avatar={
                    <Avatar
                        style={{
                            fontSize: '12px',
                            width: '22px',
                            height: '22px',
                            backgroundColor: ColorConstants.AvatarColor,
                            color: ColorConstants.WhiteAlien,
                        }}
                    >
                        <AddIcon />
                    </Avatar>
                }
                disabled={isDisabled}
                label={<span style={{ fontSize: '12px', lineHeight: '21px' }}>Add Value</span>}
            />

            {/* Values table */}
            {dataSource && (
                <MuiGridWithConnector
                    dataSource={dataSource}
                    featureColumns={columns}
                    apiRef={apiRef}
                    processRowUpdate={() => {}}
                    isSaving={isSaving}
                    columnVisibilityModel={columnVisibilityModel}
                    filterModel={filterModel}
                    onRowClick={onRowClick}
                />
            )}

            {!dataSource && (
                <Typography variant='body2' sx={{ marginLeft: '10px', color: 'text.secondary' }}>
                    {attributesGlobalId ? 'Loading values...' : 'No values.'}
                </Typography>
            )}

            {/* Map toolbar — portals Fab buttons to the map when adding or editing geometry */}
            {(isAddingValue || isEditingGeometry) && (
                <ValuesToolbar
                    editing={isEditingGeometry}
                    existing={Boolean(editingGeometryRow)}
                    onCancel={isEditingGeometry ? handleCancelGeometryEdit : handleAddValueCancel}
                    onDrawComplete={handleDrawComplete}
                    onConfirm={isEditingGeometry ? handleConfirmGeometryEdit : undefined}
                    onCopyFromFirePerimeter={handleCopyFromFirePerimeter}
                    onShapefileSelected={handleShapefileSelected}
                    isPostFire={true}
                />
            )}

            {/* Values detail panel for editing/creating */}
            {isEditingValue && selectedRow && (
                <ValuesDetailsPanel
                    open={isEditingValue}
                    valueRow={selectedRow}
                    geometry={newValueGeometry}
                    attributesGlobalID={attributesGlobalId}
                    handleCloseValues={handleCloseValuesDetail}
                    openMethod={() => setIsEditingValue(true)}
                    onSaveComplete={handleOnSaveComplete}
                    maxRank={maxRank}
                />
            )}

            {/* Success/Error notification snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                sx={{ top: '80px !important' }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    icon={snackbar.severity === 'success' ? <CheckCircleIcon /> : undefined}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
